import AppContainer from '../AppContainer';
import { useState, useEffect, useContext, useCallback } from 'react';
import {
  IonInput,
  IonTextarea,
  IonButton,
  IonProgressBar,
  IonBreadcrumb,
  IonBreadcrumbs,
  IonModal,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonIcon,
  ItemReorderEventDetail,
  IonSelect,
  IonSelectOption,
  IonGrid,
  IonRow,
  IonCol,
  IonItem,
  IonAvatar,
  IonLabel
} from '@ionic/react';
import GlobalContext from '../domain/GlobalContext';
import { api } from '../domain/ApiService';
import { Artifact, Assistant, Folder, Initiative, KnowledgeBaseItem, ThreadSummary } from '../domain/Types';
import { useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';
import { chatbubbleOutline, chatbubblesOutline, desktop, rocket, settingsOutline } from 'ionicons/icons';
import Image from '../components/Image';
import Menu from '../components/Menu';
import ListView from '../components/ListView';
import { v4 as uuidv4 } from 'uuid';
import ReactMarkdown from 'react-markdown';
import '../theme/pages.css';
import '../theme/chat.css';
import Time from '../components/Time';

const Page: React.FC = () => {

  const { user, users, initiative, setInitiative, getInitiative, initiatives, setInitiatives } = useContext(GlobalContext);
  const [ isFetched, setIsFetched ] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState<string>("");
  const [template, setTemplate] = useState<Initiative | undefined>(undefined);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showAssistantModal, setShowAssistantModal] = useState(false);

  const [selectedOwners, setSelectedOwners] = useState<string[]>([ user?.auth_id ? user.auth_id : ""]);
  const [selectedCollaborators, setSelectedCollaborators] = useState<string[]>(['allUsers']);

  const [ folderConfig, setFolder ] = useState<Folder | null>(null);
  const [ assistant, setAssistant ] = useState<Assistant | null>(null);

  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    setIsFetched(true);

    if (initiative) {

      if ((!initiative.assistants || initiative.assistants.length === 0 || initiative.template) && !location.pathname.includes("/settings")) {

        history.push("/"+ initiative.slug +"/settings");
        return;
      }

      fetchThreadSummaries();
      fetchArtifacts();

      if (initiative.template_uid != null)  
        setTemplate(getInitiative(initiative.template_uid));

      setSelectedOwners(initiative?.owners ? initiative?.owners : [ user?.auth_id ? user.auth_id : ""])
      setSelectedCollaborators(initiative?.collaborators ?  initiative?.collaborators : ['allUsers']);

      if (initiative.description != null && initiative.image == null)
        generateImage();
    }

  }, [initiative]); 

  const fetchArtifacts = useCallback(async () => {
  
    try {

      if (!initiative?.assistants) return;

      const response: Artifact[] = await api('/artifact/list', {}, initiative);

      setIsFetched(false);
      for (const assistant of initiative?.assistants) {
          if (!assistant) continue;
      
          assistant.artifacts = response.filter(artifact => artifact.assistant_uid === assistant.uid);
      }
      setIsFetched(true);

    } catch (err) {
      console.error('Error fetching:', err);
    }
  
  }, [initiative]);

  const [threadSummaries, setThreadSummaries] = useState<ThreadSummary[]>([]);
  const fetchThreadSummaries = useCallback(async () => {
  
    try {

      if (!initiative) return;
      const response: ThreadSummary[] = await api('/assistant/threadsummaries', { "initiative_id": initiative?.uid }, initiative);
      setThreadSummaries(response); 
    } catch (err) {
      console.error('Error fetching:', err);
    }
  
  }, [initiative]);

  const handleReorder = (event: CustomEvent<ItemReorderEventDetail>) => {
    // Existing reorder logic
    console.log('Dragged from index', event.detail.from, 'to', event.detail.to);
    event.detail.complete();
  }; 

  const handleAssistantEdit = (item: Assistant | null) => {
    let editItem = item;
    if (editItem == null) {
      editItem = {  "uid": uuidv4(), "name": "", "instructions": "", "slug": "", "knowledge_base": [] };
    }
    setAssistant(editItem);
    setShowAssistantModal(true);
  };

  const handleAssistantClick = (item: Assistant) => {

    if (folderConfig?.slug)
      history.push('/'+ initiative?.slug +'/'+ folderConfig?.slug +'/'+ item.slug, {});
    else
      history.push('/'+ initiative?.slug +'/'+ item.slug, {});
  };

  const [imageLoading, setImageLoading] = useState(false);
  const generateImage = async () => {
    if (initiative == null) return;
    setImageLoading(true);
    initiative.image = undefined;
    let image = await api(
      "/initiative/image",
      { image_prompt: initiative?.description },
      initiative
    );
    initiative.image = image;

    const response = await api("/initiative/list", {}, initiative);
    setInitiatives(response);
    setInitiative({ ...initiative });

    window.dispatchEvent(new Event('initiativesLoaded'));      
    window.dispatchEvent(new Event('initiativeUpdated'));

    setImageLoading(false);
  };

  const openDeliverable = async () => {

    history.push("./"+ initiative?.slug +"/share")
  }

  const openChat = async () => {


    if (initiative && initiative.assistants && initiative.assistants.length > 0)
      history.push("./"+ initiative?.slug +"/"+ initiative.assistants[0].slug);
  }

  const saveAsTemplate = async () => {

    if (!templateName) return;
    setIsFetched(false);
    try { 

      setInitiative(await api("/initiative/template", { "from_initiative": initiative?.id, "template_name": templateName }, initiative) as Initiative); 
      window.dispatchEvent(new Event('initiativeUpdated'));
    }
    catch (err) { console.error('Error:', err); }
    setShowModal(false);
    setShowTemplateModal(false);
    setIsFetched(true);
  };

  useEffect(() => {
    if (selectedCollaborators.length === 0) {
      setSelectedCollaborators(['allUsers']);
    }
  }, [selectedCollaborators]);  

  interface DataContainer {
    initiative: Initiative;
    template: Initiative;
  }
  
  function getNestedKnowledgeBaseItems(data: DataContainer, template: boolean = false): KnowledgeBaseItem[] {
    // Determine the base data source based on the 'template' flag
    const baseData = template ? data.template : data.initiative;
    if (!baseData) return [];
    
    // Collect knowledge bases from assistants directly under the base data
    const assistantKnowledgeBases: KnowledgeBaseItem[] = (baseData.assistants || []).flatMap((ass: Assistant) =>
      (ass.knowledge_base || []).map(kb => ({ ...kb, context_name: ass.name }))
    );
  
    // Collect knowledge bases from folders, including those from assistants within folders
    const folderKnowledgeBases: KnowledgeBaseItem[] = (baseData.folders || []).reduce((acc: KnowledgeBaseItem[], folder: Folder) => {
      // Add knowledge base from the folder itself if present
      if (folder.knowledge_base) {
        acc.push(...folder.knowledge_base.map(kb => ({ ...kb, context_name: folder.name })));
      }
      
      // Add knowledge bases from each assistant in the folder
      const assistantKbs = (folder.assistants || []).flatMap((ass: Assistant) =>
        (ass.knowledge_base || []).map(kb => ({ ...kb, context_name: `${folder.name} / ${ass.name}` }))
      );
      return acc.concat(assistantKbs);
    }, []);
  
    // Combine all knowledge bases
    return [...assistantKnowledgeBases, ...folderKnowledgeBases];
  }  

  if (document || (!(user?.email?.includes("laurea.fi")) && !(user?.email?.includes("oi.fi")) && !(user?.email?.includes("nbforum")) && !(user?.email?.includes("staffpoint")))) {

    const initiativeKnowledgeBaseItems = getNestedKnowledgeBaseItems({ initiative: initiative!, template: template! }, false);
    const templateKnowledgeBaseItems = getNestedKnowledgeBaseItems({ initiative: initiative!, template: template! }, true);  

    return (
      <>
      {(isFetched && initiative) ? (
          <>

            {(initiative.initiative_type == 'content') ? (
              <>
              </>
            ) : (
              <>

              <IonGrid>
                <IonRow style={{ display: 'flex', alignItems: 'stretch' }}>
                  <IonCol style={{ display: 'flex' }}>
                    <IonCard className='filled' style={{ padding: '5px', flexGrow: 1 }}>
                      <IonCardContent>
                        <div className='chat-container' style={{maxHeight: "450px"}}>

                          {threadSummaries.length === 0 && (
                            <>
                            <IonIcon icon={chatbubblesOutline} className="menu" color="medium" style={{ scale: "300%", margin: "20px" }} />
                            <p>
                            There's no progress yet. Start by chatting with your assistants to get things moving.
                            </p>
                            { initiative && initiative?.assistants && initiative?.assistants.length > 0 && 
                            <div style={{marginTop: "13px"}}>
                              <IonButton onClick={openChat}>Start: {initiative.assistants[0].name}</IonButton>
                            </div>
                            }
                            </>
                          )}

                          {threadSummaries?.map((threadSummary, index) => (
                            <>
                            <IonItem color="none" lines="none">
                              <IonAvatar className="chat-avatar">
                                <Image src={(threadSummary as any)["assistant_image"]} avatar={true} />            
                              </IonAvatar>
                              <IonLabel>
                                  <div className="chat-avatar-label">
                                    {(threadSummary as any)["assistant_name"]} 
                                    <span style={{fontSize: "90%", opacity: "0.5", paddingLeft: "7px"}}>
                                      <Time timestamp={threadSummary?.timestamp}/>
                                    </span>
                                  </div>
                                </IonLabel>
                            </IonItem>
                            <div className='chat-message'>
                              {threadSummary?.text}
                            </div>
                            </>
                          ))}
                        </div>
                      </IonCardContent>
                    </IonCard>

                  </IonCol>
                  <IonCol size='6' style={{ display: 'flex' }}>
                    <IonCard className='filled' style={{ padding: '5px', flexGrow: 1 }}>
                      <IonCardContent>

                      <IonItem color="none" lines="none">
                        <div style={{width: "160px", height: "170px", marginRight: "20px", marginTop: "10px"}}>
                        <Image
                            src={initiative.image}
                            width="160px"
                            isLoading={imageLoading}
                            setIsLoading={setImageLoading}
                          />
                        </div>
                        <div style={{ fontSize: "90%",
                                      lineHeight: "1.5",
                                      display: "-webkit-box",
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      WebkitLineClamp: 5 }}>
                          {initiative.description}
                        </div>
                      </IonItem>

                      <div>
                        <IonItem color="none" lines="none" style={{ marginTop: "15px", marginLeft: "2px" }}>
                          <big>{initiative.deliverable ? initiative.deliverable : "Deliverable"}</big>
                        </IonItem>
                        {initiative.assistants?.map((assistant, index) => (
                          <IonItem color="none" lines="none">
                              <div style={{marginLeft: "2px"}}>
                                <big>{assistant.artifacts?.length}</big>&nbsp;&nbsp;
                                <span style={{opacity: "0.6"}}>{assistant.name}</span>
                              </div>
                          </IonItem>
                        ))}
                        <div style={{margin: "13px"}}>
                          <IonButton onClick={openDeliverable} className='basic'>View and export</IonButton>
                        </div>
                      </div>                      
                      </IonCardContent>
                    </IonCard>
                  </IonCol>
                </IonRow>
              </IonGrid>

              {(initiative.initiative_type == 'client') ? (
                <></>
              ) : (
                <IonCard className='filled' style={{ padding: "10px" }}>
                  <IonCardHeader>
                    <IonCardTitle>Assistants</IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                      {initiative ? (
                        <>
                          <ListView<Assistant>
                            items={initiative.assistants}
                            columns={[
                              { attribute: 'name', label: 'Name', size: "small", avatar: "image", primary: true },
                              { attribute: 'instructions', label: 'Instructions', size: "large" }
                            ]}
                            showActions={false}
                            enableReorder={false}
                            defaultLayout={'cards'}
                            onEdit={handleAssistantEdit}
                            onReorder={handleReorder}
                            onClick={handleAssistantClick}
                            />
                        </>
                      ) : (
                        <></>
                      )}
                  </IonCardContent>
                </IonCard>
              )}
            
            </>
            )}
          </>
        ) : (

          <IonProgressBar type="indeterminate" color="medium"></IonProgressBar>

      )}
      </>      
    );
  }
  else {

    return (<></>);
  }  
}

const Breadcrumb: React.FC = () => {

  const { user, initiative } = useContext(GlobalContext);

  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter(Boolean);

  let folder_slug = "";
  let assistant_slug = "";
  let settings_slug = false;

  if (pathSegments.length === 2)  {
    
    assistant_slug = pathSegments[1];
  } 
  else if(pathSegments[2] === "settings") {

    assistant_slug = pathSegments[1];
    settings_slug = true;
  }
  else {

    folder_slug = pathSegments[1];
    assistant_slug = pathSegments[2];
  }

  return (
    <IonBreadcrumbs>
      <IonBreadcrumb routerLink={"/"}>
        <IonIcon slot="start" icon={rocket}></IonIcon>
        Base
      </IonBreadcrumb>
      <IonBreadcrumb>
        <IonIcon slot="start" icon={desktop}></IonIcon>
        {initiative?.name}
      </IonBreadcrumb>
      { user?.account?.plan == "creator" || user?.account?.plan == "business" && 
      <IonBreadcrumb routerLink={"./"+ initiative?.slug +"/settings"}>
        <IonIcon slot="end" icon={settingsOutline}></IonIcon>
      </IonBreadcrumb> }
    </IonBreadcrumbs>
  );
};

const Container: React.FC<{}> = () => { return <AppContainer ContentComponent={Page} BreadcrumbComponent={Breadcrumb} MenuComponent={Menu} />; };
export default Container;